import React, { useState } from 'react';
import { translate } from '../../../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FormInput from '../../../components/FormInput';
import BasicModal from '../../../components/BasicModal';
import Toast from 'react-native-toast-message';
import moment from 'moment';
import { MyCheckbox } from '../../../components/MyCheckbox';
import { cameraService } from '../../../services/central-api/cameras';
import MyDateRangePicker from '../../../components/MyDateRangePicker';
import { useForm } from 'react-hook-form';

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
}

interface ReportModalForm {
    period: { from: ValuePiece; to: ValuePiece; };
}

type ValuePiece = Date | null;
const regex = /^[a-zA-Z0-9 +\-./:=_ãõẽíáàâêóôúüçÃÕẼÍÁÀÂÊÓÔÚÜÇ]*$/;

export default function ReportModal({ onClose, isModalVisible }: ModalProps) {
    const { styles } = useStyles(styleSheet);

    const defaultEmptyValues: ReportModalForm = {
        period: { from: new Date(), to: new Date() },
    };

    const form = useForm<ReportModalForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const [isPdf, setIsPdf] = useState<boolean>(false);
    const [name, setName] = useState<string>('');

    function isNameInvalid(value: string) {
        return value == '' || !regex.test(value);
    }

    function isFormValid() {
        return !isNameInvalid(name);
    }

    function clearValues() {
        setName('');
        setIsPdf(false);
    }

    async function formSubmit(): Promise<void | false> {
        try {
            const isValid = await form.trigger();

            if (!isValid) {
                throw new Error('invalid form');
            }

            const values = form.getValues();

            values.period.to!.setHours(23, 59, 59);
            values.period.from!.setHours(0, 0, 0);

            await cameraService.createReport({
                name: name,
                isPdf: isPdf,
                startDate: moment(values.period.from).format('YYYY-MM-DD'),
                endDate: moment(values.period.to).format('YYYY-MM-DD'),
            });

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            clearValues();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return false;
        }
    }

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={(visible) => {
                if (!visible) {
                    onClose();
                }
            }}
            title={translate('offlineCamerasReport')}
            width='medium'
            mobileHeight='fit'
            cancelButton={{ onClick: clearValues }}
            primaryButton={{
                text: translate('download'),
                onClick: formSubmit,
                disabled: !isFormValid(),
            }}
            disableScrollView={true}
        >
            <FormInput
                label={translate('name')}
                placeholder={translate('name')}
                value={name}
                onChangeText={setName}
                invalid={isNameInvalid}
                required
                errorMessage={translate('invalidCharacters')}
            />
            <MyDateRangePicker
                label={translate('period')}
                maxDate={moment().toDate()}
                form={form}
                fieldName='period'
                zIndex={9}
                required={true}
            />
            <MyCheckbox
                style={styles.checkbox}
                label={translate('PDF')}
                checked={isPdf}
                setChecked={(value) => setIsPdf(value)} />
        </BasicModal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    checkbox: {
        paddingHorizontal: 8,
        height: 40,
        fontSize: 16,
    },
    tagsLabel: {
        color: theme.colors.labelColor,
        fontSize: 16,
    },
}));
